<script>
	import lostFarmImage from '$lib/assets/golf/coastal-links-aerial.jpg?enhanced';
	import parklandsImage from '$lib/assets/golf/country-club-tasmania-parklands.jpg?enhanced';
	import rathoImage from '$lib/assets/golf/ratho-farm-green.jpg?enhanced';
	import sevenMileImage from '$lib/assets/golf/seven-mile-beach-aerial.jpg?enhanced';

	// Course notes are drawn from the tour's own itinerary — nothing here claims
	// anything the tour content doesn't already say.
	const courses = [
		{
			name: 'Lost Farm',
			region: 'Barnbougle',
			note: 'A spectacular course known for its dramatic coastal setting and world-class design.',
			image: lostFarmImage,
			alt: 'Aerial view of a links golf course on a dune-covered coastal peninsula at sunrise',
			feature: true
		},
		{
			name: 'Championship Golf Course',
			region: 'Launceston',
			note: 'A morning round on this newly developed parklands-style course before the journey south begins.',
			image: parklandsImage,
			alt: 'A bunkered green at the Championship Golf Course, Launceston'
		},
		{
			name: 'Ratho Farm',
			region: 'Bothwell',
			note: "Australia's oldest golf course — rich in character and history.",
			image: rathoImage,
			alt: 'A green at Ratho Farm golf course in Bothwell',
			badge: "Australia's oldest"
		},
		{
			name: '7 Mile Beach',
			region: 'Hobart',
			note: "One of southern Tasmania's most picturesque coastal courses, with a links-style feel.",
			image: sevenMileImage,
			alt: 'Aerial view of 7 Mile Beach golf course and coastline',
			wide: true
		}
	];
</script>

<section id="golf" class="bg-white pb-16 lg:pb-24">
	<div class="mx-auto max-w-7xl px-4">
		<div class="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
			<div class="max-w-2xl">
				<p class="text-sm font-semibold uppercase tracking-widest text-[#194139]">The golf</p>
				<h2 class="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
					Four golf experiences, from wild coastal links to Australia's oldest course
				</h2>
			</div>
			<p class="max-w-sm leading-7 text-gray-700">Green fees are included at every one of them.</p>
		</div>

		<div class="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
			{#each courses as course, i}
				<article
					class={`group relative flex min-h-[320px] flex-col justify-end overflow-hidden rounded-2xl p-6 ${
						course.feature
							? 'sm:col-span-2 lg:col-span-1 lg:row-span-2 lg:min-h-[664px]'
							: course.wide
								? 'sm:col-span-2'
								: ''
					}`}
				>
					<enhanced:img
						src={course.image}
						alt={course.alt}
						class="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
						sizes={course.wide
							? '(min-width: 1024px) 66vw, 100vw'
							: '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'}
						loading="lazy"
					/>
					<div
						class="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent"
					></div>

					<span
						class="absolute left-6 top-6 font-heading text-2xl font-bold text-white/40"
						aria-hidden="true"
					>
						{String(i + 1).padStart(2, '0')}
					</span>
					{#if course.badge}
						<span
							class="absolute right-6 top-6 rounded-full bg-[#ffd451] px-3 py-1 text-xs font-semibold text-foreground"
						>
							{course.badge}
						</span>
					{/if}

					<!-- Wide cards keep the text at single-card width. -->
					<div class={`relative ${course.wide ? 'max-w-sm' : ''}`}>
						<p class="text-xs font-semibold uppercase tracking-[0.2em] text-[#ffd451]">
							{course.region}
						</p>
						<h3
							class={`mt-2 font-heading font-bold text-white ${course.feature ? 'text-3xl lg:text-4xl' : 'text-2xl'}`}
						>
							{course.name}
						</h3>
						<p class="mt-2 text-sm leading-6 text-white/85">{course.note}</p>
					</div>
				</article>
			{/each}
		</div>
	</div>
</section>
